import * as React from 'react';
import { dateToString, displayPhoneNumber, notEmpty } from 'src/utils/utils';
import { GetDayAppointmentsQuery } from '../../__generated__/WorkOrderCalendarQueries.generated';
import { Avatar, AvatarFallback } from 'src/shadcn/components/ui/avatar';
import { PersonIcon } from '@radix-ui/react-icons';
import Link from 'next/link';
import {
  CalendarDays,
  Car,
  CarFront,
  FilePenLine,
  FileSignature,
  Plus,
  Trash,
  UserPlus,
  UserRoundSearch,
  Wrench,
} from 'lucide-react';
import { diContainer } from 'src/dependencyInjection/diContainer';
import { IBrowserHistory } from 'src/services/BrowserHistory/IBrowserHistoryService';
import { DI_TYPES } from 'src/dependencyInjection/diTypes';
import { RouterLinks } from 'src/routing/routerLinks';
import { Button } from 'src/shadcn/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/shadcn/components/ui/dropdown-menu';
import { SelectExistingCustomerPopover } from 'src/Components/WorkOrdersPage/WorkOrderPage/CustomerSelection/SelectExistingCustomerPopover';
import { JobSelectCustomerModal } from 'src/Components/WorkOrdersPage/WorkOrderPage/JobView/JobSelectCustomerModal';
import { SelectCarModal } from 'src/Components/WorkOrdersPage/WorkOrderPage/JobView/SelectCarModal.tsx/SelectCarModal';
import { AppointmentCreateNewCarModal } from './AppointmentCreateNewCarModal';
import { gql } from '@apollo/client';
import { useCreateCarForAppointmentMutation } from './__generated__/CustomerCard.generated';
import { useHotToast } from 'src/utils/useHotToast';
import { CreateCarInput } from 'src/graphql/__generated__/globalTypes';
import * as uuid from 'uuid';

type Appointment =
  GetDayAppointmentsQuery['getAppointmentsForDropoffRange']['items'][0];
type Customer = NonNullable<Appointment['customer']>;
type Car = NonNullable<Appointment['car']>;

export interface ICustomerCardProps {
  customer: Customer | undefined;
  car: Car | undefined;
  setCustomer: (customer: Customer | undefined) => void;
  setCar: (car: Car | undefined) => void;
}

export const CustomerCard: React.FunctionComponent<
  ICustomerCardProps
> = props => {
  const customer = props.customer;
  const [
    isSelectExistingCustomerModalOpen,
    setIsSelectExistingCustomerModalOpen,
  ] = React.useState(false);
  const [isCreateNewCustomerModalOpen, setIsCreateNewCustomerModalOpen] =
    React.useState(false);
  const [isSelectCarModalOpen, setIsSelectCarModalOpen] = React.useState(false);
  const [isCreateNewCarModalOpen, setIsCreateNewCarModalOpen] =
    React.useState(false);
  const { createCar } = useCreateCarForAppointment();
  return (
    <>
      <div className='relative flex flex-col items-center rounded border p-5'>
        {props.customer && (
          <div className='absolute right-3 top-3 flex gap-3'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={'ghost'}
                  title='Select a car'
                  className='flex h-8 w-8 p-0 data-[state=open]:bg-muted'
                >
                  <Car className='size-4' />
                  <span className='sr-only'>Select a car</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuGroup>
                  {props.car && (
                    <>
                      <DropdownMenuItem
                        className='hover:bg-gray-100'
                        onClick={e => {
                          setIsCreateNewCarModalOpen(true);
                        }}
                      >
                        <FileSignature className='mr-2 h-4 w-4' />
                        <span>Edit car details</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className='hover:bg-gray-100'
                        onClick={e => {
                          props.setCar(undefined);
                        }}
                      >
                        <Trash className='mr-2 h-4 w-4' />
                        <span>Remove car</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}

                  <DropdownMenuItem
                    asChild
                    className='hover:bg-gray-100'
                    onClick={e => {
                      setIsSelectCarModalOpen(true);
                    }}
                  >
                    <div className='flex'>
                      <CarFront className='mr-2 h-4 w-4' />
                      <span>Select an existing car</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    asChild
                    className='hover:bg-gray-100'
                    onClick={e => {
                      if (props.customer) {
                        const carId = uuid.v4();
                        createCar({
                          id: carId,
                          customerId: props.customer.id,
                        });
                        props.setCar({ id: carId, createdAt: new Date() });
                        setIsCreateNewCarModalOpen(true);
                      }
                    }}
                  >
                    <div className='flex'>
                      <Plus className='mr-2 size-4' />
                      <span>Create a new car</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  title='Replace customer'
                  variant={'ghost'}
                  className='flex h-8 w-8 p-0 data-[state=open]:bg-muted'
                >
                  <FilePenLine className='size-4' />
                  <span className='sr-only'>Replace customer</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    className='hover:bg-gray-100'
                    onClick={e => {
                      setIsSelectExistingCustomerModalOpen(true);
                    }}
                  >
                    <UserRoundSearch className='mr-2 h-4 w-4' />
                    <span>Select an existing customer</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className='hover:bg-gray-100'
                    onClick={e => {
                      setIsCreateNewCustomerModalOpen(true);
                    }}
                  >
                    <UserPlus className='mr-2 h-4 w-4' />
                    <span>Create a new customer</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    className='hover:bg-gray-100'
                    onClick={e => {
                      props.setCustomer(undefined);
                    }}
                  >
                    <Trash className='mr-2 h-4 w-4' />
                    <span>Remove customer</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        <Avatar className='mb-5'>
          <AvatarFallback>
            <PersonIcon className='size-5' />
          </AvatarFallback>
        </Avatar>
        {!customer && (
          <>
            <h4 className='py-5 text-sm font-semibold'>No customer selected</h4>
            <div className='flex flex-col gap-3 md:flex-row'>
              <Button
                variant={'outline'}
                onClick={e => {
                  e.preventDefault();
                  setIsSelectExistingCustomerModalOpen(true);
                }}
              >
                <UserRoundSearch className='mr-2 size-4' />
                <span>Select an existing customer</span>
              </Button>
              <Button
                variant={'outline'}
                onClick={e => {
                  e.preventDefault();
                  setIsCreateNewCustomerModalOpen(true);
                }}
              >
                <UserPlus className='mr-2 size-4' />
                <span>Create a new customer</span>
              </Button>
            </div>
          </>
        )}
        {customer && (
          <>
            <Link
              href={diContainer
                .get<IBrowserHistory>(DI_TYPES.BrowserHistoryService)
                .getUri(RouterLinks.Customers, [customer.id])}
            >
              <h4 className='text-base font-semibold hover:underline'>
                {[customer.firstName, customer.lastName]
                  .filter(notEmpty)
                  .join(' ')}
              </h4>
              {customer.companyName && (
                <h4 className='text-sm font-semibold text-muted-foreground'>
                  {customer.companyName}
                </h4>
              )}
            </Link>
            {customer.email && <p className='text-sm'>{customer.email}</p>}
            {customer.phoneNumber && (
              <p className='text-sm'>
                {displayPhoneNumber(customer.phoneNumber)}
              </p>
            )}
            {customer.secondaryPhoneNumber && (
              <p className='text-sm'>
                {displayPhoneNumber(customer.secondaryPhoneNumber)}
              </p>
            )}
            {props.car && (
              <>
                <div className='flex flex-col items-center py-5 italic'>
                  <p className='text-sm'>
                    {(() => {
                      const carTitle = [
                        props.car.year,
                        props.car.make,
                        props.car.model,
                      ]
                        .filter(notEmpty)
                        .join(' ');
                      if (carTitle) {
                        return carTitle;
                      } else {
                        return 'Car selected, please edit above.';
                      }
                    })()}
                  </p>
                  {props.car.colour && (
                    <p className='text-sm'>Colour: {props.car.colour}</p>
                  )}
                  {props.car.vin && (
                    <p className='text-sm'>Vin: {props.car.vin}</p>
                  )}
                  {props.car.licensePlate && (
                    <p className='text-sm'>
                      License plate: {props.car.licensePlate}
                    </p>
                  )}
                </div>
                <div className='flex items-center pt-2'>
                  <Wrench className='mr-2 h-4 w-4 opacity-70' />{' '}
                  <span className='text-xs text-muted-foreground'>
                    Vehicle first serviced{' '}
                    {dateToString(new Date(props.car?.createdAt), {
                      removeTime: true,
                    })}
                  </span>
                </div>
              </>
            )}
            <div className='flex items-center pt-2'>
              <CalendarDays className='mr-2 h-4 w-4 opacity-70' />{' '}
              <span className='text-xs text-muted-foreground'>
                Customer since{' '}
                {dateToString(new Date(customer.createdAt), {
                  removeTime: true,
                })}
              </span>
            </div>
          </>
        )}
      </div>
      <SelectExistingCustomerPopover
        open={isSelectExistingCustomerModalOpen}
        setOpen={setIsSelectExistingCustomerModalOpen}
        onSelectCustomer={customer => {
          props.setCustomer(customer);
          setIsSelectExistingCustomerModalOpen(false);
        }}
      />
      <JobSelectCustomerModal
        isOpen={isCreateNewCustomerModalOpen}
        setIsOpen={setIsCreateNewCustomerModalOpen}
        onSelectCustomer={customer => {
          props.setCustomer(customer);
          setIsCreateNewCustomerModalOpen(false);
        }}
      />
      {props.customer && (
        <SelectCarModal
          onSelectCar={car => {
            props.setCar(car);
            setIsSelectCarModalOpen(false);
          }}
          customerId={props.customer.id}
          isOpen={isSelectCarModalOpen}
          setIsOpen={setIsSelectCarModalOpen}
        />
      )}
      {isCreateNewCarModalOpen && props.car && (
        <AppointmentCreateNewCarModal
          isOpen={isCreateNewCarModalOpen}
          setIsOpen={setIsCreateNewCarModalOpen}
          car={props.car}
          onCarUpdated={newCar => {
            props.setCar(newCar);
          }}
        />
      )}
    </>
  );
};

function useCreateCarForAppointment() {
  const toast = useHotToast();
  const [createCar, { loading: isCreatingCar }] =
    useCreateCarForAppointmentMutation({
      onError: error => {
        toast.error(error.message);
      },
    });
  return {
    createCar: (input: CreateCarInput) => {
      return createCar({
        variables: {
          car: input,
        },
      });
    },
    isCreatingCar,
  };
}
const mutation = gql`
  mutation CreateCarForAppointment($car: CreateCarInput!) {
    createCar(car: $car) {
      id
      vin
      make
      model
      year
      colour
      licensePlate
    }
  }
`;
